import { getTciBand } from "./bands";
import type { TciInputs, TciResult } from "./types";

function toFiniteNumber(value: unknown): number {
  const n = typeof value === "number" ? value : Number(value);
  return Number.isFinite(n) ? n : 0;
}

function clampPercent(value: number): number {
  return Math.min(100, Math.max(0, value));
}

function nonNegative(value: number): number {
  return Math.max(0, value);
}

/**
 * Pure TCI calculation.
 * Percentages are 0–100; invalid/NaN inputs normalize to 0.
 */
export function calculateTci(inputs: TciInputs): TciResult {
  const headcount = nonNegative(toFiniteNumber(inputs.labor.headcount));
  const timePercent = clampPercent(toFiniteNumber(inputs.labor.timePercent));
  const salary = nonNegative(toFiniteNumber(inputs.labor.salary));

  const yieldVolume = nonNegative(toFiniteNumber(inputs.yieldPenalty.volume));
  const penaltyPerEvent = nonNegative(
    toFiniteNumber(inputs.yieldPenalty.penaltyPerEvent),
  );

  const untappedVolume = nonNegative(toFiniteNumber(inputs.untapped.volume));
  const actionablePercent = clampPercent(
    toFiniteNumber(inputs.untapped.actionablePercent),
  );
  const valuePerAction = nonNegative(
    toFiniteNumber(inputs.untapped.valuePerAction),
  );

  const annualEbitda = toFiniteNumber(inputs.annualEbitda);

  const laborWaste = headcount * (timePercent / 100) * salary;
  const yieldPenalty = yieldVolume * penaltyPerEvent;
  const untappedValue =
    untappedVolume * (actionablePercent / 100) * valuePerAction;
  const totalTrapped = laborWaste + yieldPenalty + untappedValue;

  if (annualEbitda <= 0) {
    return {
      laborWaste,
      yieldPenalty,
      untappedValue,
      totalTrapped,
      tciScore: null,
      band: null,
    };
  }

  const rawScore = (totalTrapped / annualEbitda) * 100;
  const tciScore = Math.min(100, Math.max(0, rawScore));

  return {
    laborWaste,
    yieldPenalty,
    untappedValue,
    totalTrapped,
    tciScore,
    band: getTciBand(tciScore),
  };
}
